import { createPortal } from "react-dom";
import { IconClose } from "@/components/icons/PageActionIcons";
import type { AssistantTask, AssistantTaskStep } from "./assistantTaskTypes";
import { fmtWhen } from "@/utils/format";

function StepIcon({ status }: { status: AssistantTaskStep["status"] }) {
  if (status === "completed") {
    return (
      <span className="asstTaskStepIcon asstTaskStepIcon--done" aria-hidden="true">
        ✓
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span className="asstTaskStepIcon asstTaskStepIcon--fail" aria-hidden="true">
        !
      </span>
    );
  }
  if (status === "active") {
    return <span className="asstTaskStepIcon asstTaskStepIcon--active" aria-hidden="true" />;
  }
  return <span className="asstTaskStepIcon asstTaskStepIcon--pending" aria-hidden="true" />;
}

type Props = {
  open: boolean;
  task: AssistantTask | null;
  onClose: () => void;
  onOpenPage?: (path: string) => void;
};

export default function AssistantTaskModal({ open, task, onClose, onOpenPage }: Props) {
  if (!open || !task) return null;

  const running = task.status === "running";
  const failed = task.status === "failed";
  const done = task.status === "completed";
  const targetPath = String(task.meta?.targetPath || task.result?.targetPath || "");
  const previewUrl = String(task.result?.previewUrl || "");

  return createPortal(
    <div
      className="asstTaskModalBackdrop open"
      role="dialog"
      aria-modal="true"
      aria-labelledby="asstTaskModalTitle"
      onClick={(e) => {
        if (e.target === e.currentTarget && !running) onClose();
      }}
    >
      <div className="asstTaskModal" onClick={(e) => e.stopPropagation()}>
        <div className="asstTaskModalHead">
          <div className="asstTaskModalHeadText">
            <div className="asstTaskModalEyebrow">Assistant task</div>
            <h2 id="asstTaskModalTitle">{task.title}</h2>
            {task.subtitle ? <p className="asstTaskModalSub">{task.subtitle}</p> : null}
          </div>
          <button type="button" className="iconBtn" title="Close" onClick={onClose}>
            <IconClose />
          </button>
        </div>

        <div className="asstTaskModalStatusRow">
          <span className={"asstTaskStatusPill" + (running ? " running" : done ? " done" : failed ? " fail" : "")}>
            {running ? "In progress" : done ? "Completed" : failed ? "Failed" : task.status}
          </span>
          <span className="muted mono" style={{ fontSize: 11 }}>
            Started {fmtWhen(task.startedAtMs)}
            {task.finishedAtMs ? ` · Finished ${fmtWhen(task.finishedAtMs)}` : ""}
          </span>
        </div>

        <div className="asstTaskProgressWrap">
          <div className="asstTaskProgressBar" role="progressbar" aria-valuenow={task.progress} aria-valuemin={0} aria-valuemax={100}>
            <div className="asstTaskProgressFill" style={{ width: `${Math.max(0, Math.min(100, task.progress))}%` }} />
          </div>
          <span className="asstTaskProgressPct mono">{task.progress}%</span>
        </div>

        <div className="asstTaskModalBody">
          <div className="asstTaskStepsCol">
            <h3 className="asstTaskSectionTitle">Process</h3>
            <ol className="asstTaskSteps">
              {task.steps.map((step) => (
                <li
                  key={step.id}
                  className={
                    "asstTaskStep" +
                    (step.status === "active" ? " active" : "") +
                    (step.status === "completed" ? " done" : "") +
                    (step.status === "failed" ? " fail" : "")
                  }
                >
                  <StepIcon status={step.status} />
                  <div className="asstTaskStepBody">
                    <div className="asstTaskStepLabel">{step.label}</div>
                    {step.detail ? <div className="asstTaskStepDetail mono">{step.detail}</div> : null}
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="asstTaskLogCol">
            <h3 className="asstTaskSectionTitle">Activity log</h3>
            <div className="asstTaskLog">
              {task.logs.length === 0 ? (
                <div className="muted">Waiting for events…</div>
              ) : (
                task.logs.map((log, i) => (
                  <div key={i} className={"asstTaskLogLine" + (log.level === "error" ? " err" : "")}>
                    <span className="asstTaskLogTs mono">{fmtWhen(log.ts)}</span>
                    <span>{log.message}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {failed && task.error ? (
          <div className="asstTaskErrorBanner">{task.error}</div>
        ) : null}

        {done && (previewUrl || targetPath) ? (
          <div className="asstTaskSuccessBanner">
            Page is live{targetPath ? ` at ${targetPath}` : ""}.
          </div>
        ) : null}

        <div className="asstTaskModalFoot">
          {done && previewUrl ? (
            <a className="primaryBtn" href={previewUrl} target="_blank" rel="noopener noreferrer">
              Open page
            </a>
          ) : null}
          {done && targetPath && onOpenPage ? (
            <button type="button" className="btn" onClick={() => onOpenPage(targetPath)}>
              View in Pages tab
            </button>
          ) : null}
          <button type="button" className="btn" onClick={onClose}>
            {running ? "Run in background" : "Close"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
